import React from 'react';
import { NextBestAction } from './NextBestAction';

/**
 * EmptyState Component
 *
 * Liquid Design Policy: No page should show "0" without meaning.
 * Every empty state must include:
 * - Why it's empty (not configured / no data / filtered out)
 * - What to do next (1 primary CTA)
 * - What will appear later (reassurance)
 *
 * Usage:
 * <EmptyState
 *   type="no-data" // no-data | not-configured | filtered-out | in-progress
 *   title="No profiles yet"
 *   reason="Upload resumes to start building your talent pool"
 *   action="Upload Profiles"
 *   onAction={() => navigate('/source')}
 *   preview="Profiles will appear here with skills, experience, and match scores"
 *   icon="profiles"
 * />
 */

const iconMap = {
  profiles: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  ),
  matches: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  shortlist: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  interviews: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  jd: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  search: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  settings: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  ai: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  email: (
    <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
};

const typeStyles = {
  'no-data': {
    bg: 'bg-muted border border-line-subtle',
    icon: 'text-ink-subtle',
    badge: 'bg-subtle text-ink-muted border border-line',
    badgeText: 'No data yet',
  },
  'not-configured': {
    bg: 'bg-muted border border-line-subtle',
    icon: 'text-amber-400',
    badge: 'bg-amber-500/15 text-amber-400 border border-amber-500/25',
    badgeText: 'Setup required',
  },
  'filtered-out': {
    bg: 'bg-muted border border-line-subtle',
    icon: 'text-blue-400',
    badge: 'bg-blue-500/15 text-blue-400 border border-blue-500/25',
    badgeText: 'Filtered',
  },
  'in-progress': {
    bg: 'bg-muted border border-line-subtle',
    icon: 'text-blue-400',
    badge: 'bg-blue-500/15 text-blue-400 border border-blue-500/25',
    badgeText: 'Processing',
  },
  'success': {
    bg: 'bg-green-500/[0.06] border border-green-500/20',
    icon: 'text-green-400',
    badge: 'bg-green-500/15 text-green-400 border border-green-500/25',
    badgeText: 'Complete',
  },
};

export const EmptyState = ({
  type = 'no-data',
  title,
  reason,
  action,
  onAction,
  secondaryAction,
  onSecondaryAction,
  preview,
  icon = 'profiles',
  compact = false,
  showNBA = true,
  nbaVariant = 'primary',
}) => {
  const styles = typeStyles[type] || typeStyles['no-data'];
  const IconComponent = iconMap[icon] || iconMap.profiles;

  if (compact) {
    return (
      <div className={`rounded-xl p-6 ${styles.bg} text-center`}>
        <div className={`mx-auto mb-3 ${styles.icon}`}>
          <div className="w-12 h-12 mx-auto">
            {React.cloneElement(IconComponent, { className: 'w-12 h-12' })}
          </div>
        </div>
        <p className="text-ink-muted font-medium">{title}</p>
        {reason && <p className="text-sm text-ink-subtle mt-1">{reason}</p>}
        {action && onAction && (
          <button
            onClick={onAction}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            {action}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`rounded-2xl ${styles.bg} p-8`}>
      <div className="max-w-lg mx-auto text-center">
        {/* Status Badge */}
        <div className="mb-4">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${styles.badge}`}>
            {styles.badgeText}
          </span>
        </div>

        {/* Icon */}
        <div className={`mx-auto mb-6 ${styles.icon}`}>
          {IconComponent}
        </div>

        {/* Title */}
        <h3 className="text-xl font-semibold text-ink mb-2">
          {title}
        </h3>

        {/* Reason (why it's empty) */}
        {reason && (
          <p className="text-ink-muted mb-6">
            {reason}
          </p>
        )}

        {/* NBA or simple button */}
        {action && onAction && showNBA ? (
          <div className="max-w-md mx-auto">
            <NextBestAction
              action={action}
              description={reason}
              onClick={onAction}
              variant={nbaVariant}
              icon={icon === 'profiles' ? 'upload' : icon === 'search' ? 'search' : 'arrow'}
              compact
            />
          </div>
        ) : action && onAction ? (
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={onAction}
              className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors shadow-sm"
            >
              {action}
            </button>
            {secondaryAction && onSecondaryAction && (
              <button
                onClick={onSecondaryAction}
                className="px-6 py-3 bg-surface text-ink-muted rounded-xl font-medium hover:bg-muted transition-colors border border-line"
              >
                {secondaryAction}
              </button>
            )}
          </div>
        ) : null}

        {/* Preview (what will appear) */}
        {preview && (
          <div className="mt-8 p-4 bg-muted rounded-xl border border-line/50">
            <p className="text-sm text-ink-subtle flex items-center justify-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {preview}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmptyState;
